import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormControl } from '@chakra-ui/react';
import { Select, chakraComponents } from 'chakra-react-select';
import { useSelector } from 'react-redux';

import { FormLabel } from '@/components/v4';
import { chipTagStyles } from '@/chakratheme';

// A palette of always-shown chips; clicking one inserts its `id` into the textarea at `cursorPosition`.
const ChipInsertPalette = ({
    staticEnum,
    activeTextareaID,
    activeTextareaField,
    setState,
    label,
    cursorPosition,
    setCursorPosition,
    placeholder,
    'data-walkthroughid': dataWalkthroughId,
    ...rest
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    // Ref, not state, so `components` below doesn't remount every chip on each keystroke.
    const cursorPositionRef = useRef(cursorPosition);
    useEffect(() => {
        cursorPositionRef.current = cursorPosition;
    }, [cursorPosition]);

    const handleClick = useCallback(
        (option) => {
            const inputSelected = document.getElementById(activeTextareaID);
            if (!inputSelected) return;

            const position = cursorPositionRef.current;
            const value = option.id;
            const textValue =
                inputSelected.value.substring(0, position) +
                value +
                inputSelected.value.substring(
                    position,
                    inputSelected.value.length,
                );
            setState({ [activeTextareaField]: textValue });
            inputSelected.value = textValue;
            inputSelected.focus();
            inputSelected.setSelectionRange(
                position + value.length,
                position + value.length,
            );
            setCursorPosition(inputSelected.selectionStart);
        },
        [activeTextareaID, activeTextareaField, setState, setCursorPosition],
    );

    const components = useMemo(
        () => ({
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            ClearIndicator: () => null,
            MultiValueRemove: () => null,
            MultiValueContainer: (props) => (
                <chakraComponents.MultiValueContainer
                    {...props}
                    innerProps={{
                        ...props.innerProps,
                        role: 'button',
                        tabIndex: 0,
                        // Prevents react-select's Control from stealing focus back from the field being inserted into.
                        onMouseDown: (e) => e.preventDefault(),
                        onClick: () => handleClick(props.data),
                        onKeyDown: (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                handleClick(props.data);
                            }
                        },
                    }}
                />
            ),
        }),
        [handleClick],
    );

    const resolvedLabel = label ?? 'Fields to include (Autofill)';

    return (
        <FormControl data-walkthroughid={dataWalkthroughId}>
            {resolvedLabel && <FormLabel>{resolvedLabel}</FormLabel>}
            <Select
                {...rest}
                isMulti
                value={staticEnum}
                options={staticEnum}
                onChange={() => {}}
                isSearchable={false}
                isClearable={false}
                openMenuOnClick={false}
                menuIsOpen={false}
                tabSelectsValue={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                placeholder={placeholder ?? ''}
                components={components}
                chakraStyles={{
                    control: (base) => ({
                        ...base,
                        background: darkMode ? 'dark.bgDark' : '#fff',
                        color: darkMode ? 'dark.textDark' : 'inherit',
                        borderColor: darkMode
                            ? 'dark.tcap-borderGray'
                            : 'inherit',
                    }),
                    multiValue: (base) => ({
                        ...base,
                        ...chipTagStyles(darkMode),
                        cursor: 'pointer',
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        ...chipTagStyles(darkMode),
                    }),
                }}
            />
        </FormControl>
    );
};

export default ChipInsertPalette;
